import { GetOrdersRequest } from '@api';
import type {
  OrderListCompoundFilterState,
  OrderListFilterDateOptions,
} from '../../hooks/use-order-list-compound-filter';
import { DateFilterData } from '../../components/sub-topic-options/components/date-filter';
import { LocationRadiusFilterData } from '../../components/sub-topic-options/components/location-filter';
import { DateTime } from 'luxon';

export type MapOrdersRequestValues = {
  globalSearch: string;
  filters: OrderListCompoundFilterState;
  page: number;
  pageSize: number;
  sort: GetOrdersRequest['body']['sort'];
};

export const mapOrdersRequestValues = (request: MapOrdersRequestValues): GetOrdersRequest => {
  const trimmedGlobalSearch = request.globalSearch.trim();

  const isGlobalSearchValue = Boolean(trimmedGlobalSearch.length);

  return {
    queryParams: {
      pg: request.page - 1,
      rc: request.pageSize,
    },
    body: {
      search: isGlobalSearchValue ? trimmedGlobalSearch : undefined,
      filters: mapFilters(request.filters),
      sort: request.sort,
    },
  };
};

export function mapFilters(filters: OrderListCompoundFilterState) {
  return filters.reduce<Required<GetOrdersRequest['body']>['filters']>((acc, filter) => {
    const validOptions = filter.options.filter((option) => option.stateModifiers?.isActive);
    switch (filter.id) {
      case 'status':
        acc.status = validOptions.map((option) => option.id);
        return acc;

      case 'contacts':
        acc.contacts = validOptions.map((option) => option.id);
        return acc;

      case 'dates':
        acc.dates = (validOptions as OrderListFilterDateOptions)
          .filter((option) => option.data?.dateStart)
          .map((option) => {
            const data = option.data as DateFilterData;
            const dateStart = DateTime.fromISO(data.dateStart).startOf('day');
            return {
              type: data.dateTypeValue,
              start: dateStart.toISO()!,
              end: data.dateEnd
                ? DateTime.fromISO(data.dateEnd).endOf('day').toISO()!
                : dateStart.endOf('day').toISO()!,
            };
          });
        return acc;

      case 'pickup':
        // @ts-expect-error - type is valid even though TS server says the contrary
        acc.pickup = validOptions.map((option) => {
          const { label: _, value: __, ...location } = option.data as LocationRadiusFilterData;
          return location;
        });
        return acc;
      case 'delivery':
        // @ts-expect-error - type is valid even though TS server says the contrary
        acc.delivery = validOptions.map((option) => {
          const { label: _, value: __, ...location } = option.data as LocationRadiusFilterData;
          return location;
        });
        return acc;

      case 'branches':
        acc.branches = validOptions.map((option) => option.id);
        return acc;

      default:
        return acc;
    }
  }, {});
}
